import { snapshotSchema } from "@domain/schemas/snapshot";
import {
  loadTombstones,
  mergeTombstones,
  pruneTombstones,
  saveTombstones,
  type Tombstone,
} from "@domain/sync/tombstones";
import type { AppDatabase } from ".";
import type { Setlist } from "./setlist";
import type { Song } from "./song";

export const SNAPSHOT_VERSION = 2;

export interface Snapshot {
  version: number;
  exportedAt: number;
  songs: Song[];
  setlists: Setlist[];
  tombstones?: Tombstone[];
}

export async function exportSnapshot(db: AppDatabase, profileId: string): Promise<Snapshot> {
  const [songs, setlists] = await Promise.all([db.songs.toArray(), db.setlists.toArray()]);
  const tombstones = loadTombstones(profileId);
  return {
    version: SNAPSHOT_VERSION,
    exportedAt: Date.now(),
    songs,
    setlists,
    ...(tombstones.length > 0 ? { tombstones } : {}),
  };
}

/**
 * Replace local songs and setlists with the snapshot's. With a `profileId`, the snapshot's
 * tombstones are also merged into that profile's tombstone store (minus items now present).
 */
export async function importSnapshot(
  db: AppDatabase,
  data: unknown,
  profileId?: string,
): Promise<void> {
  const snapshot = snapshotSchema.parse(data);
  await db.transaction("rw", db.songs, db.setlists, async () => {
    await db.songs.clear();
    await db.setlists.clear();
    if (snapshot.songs.length > 0) {
      await db.songs.bulkAdd(snapshot.songs);
    }
    if (snapshot.setlists.length > 0) {
      await db.setlists.bulkAdd(snapshot.setlists);
    }
  });
  if (profileId) {
    const present = new Set([
      ...snapshot.songs.map((s) => `song:${s.id}`),
      ...snapshot.setlists.map((s) => `setlist:${s.id}`),
    ]);
    const tombstones = mergeTombstones(loadTombstones(profileId), snapshot.tombstones ?? []);
    saveTombstones(
      profileId,
      pruneTombstones(tombstones).filter((t) => !present.has(`${t.type}:${t.id}`)),
    );
  }
}
